// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { getCartItemWeight, hideStrikethroughPrice } from '@iheartjane/menu';
import type { Spacing } from '@iheartjane/reefer';
import {
  Box,
  Flex,
  Image,
  JaneGoldIcon,
  TagIcon,
  Typography,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import get from 'lodash-es/get';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import type { AlgoliaPriceId, AlgoliaProduct } from '@jane/search/types';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type {
  MaterializedMenuProduct,
  PendingCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import { getProductImages } from '@jane/shared/util';

import { useKioskUser } from '../../../data-access';
import type { RootLoaderType } from '../../../data-access';
import {
  useGetProductPrices,
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../../hooks';
import {
  customCategoryLabel,
  customLineageLabel,
  isSupportedStoreSpecial,
  potencyDescription,
} from '../../../util';
import { HighlightedText } from '../HighlightedText/HighlightedText';
import { StarRating } from '../StarRating/StarRating';

interface ProductRowPropsBase {
  janeGoldTagLabel?: string | null;
  onProductImageClick?: (product: PendingCartProduct) => void;
  priceId?: AlgoliaPriceId | PriceId;
  product: AlgoliaProduct | PendingCartProduct | MaterializedMenuProduct;
  productInstance?: SmartSortProduct<AlgoliaProduct>;
  /** In the context of showing search results, the substring to highlight in a Product's listing */
  searchTerm?: string;
  showCategory?: boolean;
  showPotency?: boolean;
  showPrice?: boolean;
  showRating?: boolean;
}

interface ProductRowWithActionsProps extends ProductRowPropsBase {
  actions: ReactNode | ReactNode[];
  onClick?: never;
}

interface ClickableProductRow extends ProductRowPropsBase {
  actions?: never;
  onClick?: (product: SmartSortProduct<AlgoliaProduct>) => void;
}

/**
 * `actions` and `onClick` are mutually exclusive through these props
 * because of an axe accessibility rule about nested interactive controls.
 * https://dequeuniversity.com/rules/axe/4.4/nested-interactive?application=axeAPI
 */
export type ProductRowProps = ClickableProductRow | ProductRowWithActionsProps;

const isPendingCartProduct = (
  product: AlgoliaProduct | PendingCartProduct | MaterializedMenuProduct
): product is PendingCartProduct => 'price_id' in product;

const InViewFlex = styled(InView)<{ mb?: Spacing }>(({ mb }) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: mb,
}));

export const ProductRow = ({
  actions = null,
  productInstance,
  janeGoldTagLabel,
  onClick,
  onProductImageClick,
  priceId,
  product,
  searchTerm,
  showCategory = false,
  showPotency = false,
  showPrice = true,
  showRating = true,
}: ProductRowProps) => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: userData } = useKioskUser(undefined, {
    enabled: !!isPendingCartProduct(product),
  });
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);

  const isSupportedSpecial =
    specials
      .filter(({ id }) => product.special_id === id)
      .filter((special) => isSupportedStoreSpecial(special, true)).length > 0;

  const { images: productImages } = useMemo(
    () => getProductImages(product),
    [product]
  );
  const getProductPrice = useGetProductPrices(product, specials);
  const {
    discountPrice,
    priceId: formattedPriceId,
    unitPrice,
  } = getProductPrice(priceId);

  const shouldShowGold = useShouldKioskShowGold({ product });

  const quantity = get(product as PendingCartProduct, 'count', 1);
  const { defaultSpacing, isIpad } = useKioskSpacing();

  const productKindLabel = customCategoryLabel({
    kind: product.kind,
    store,
  });

  const showWeight =
    isPendingCartProduct(product) &&
    product.price_id !== 'each' &&
    !product.amount;
  const productWeight = showWeight ? `${getCartItemWeight(product)}` : '';

  return (
    <InViewFlex
      aria-label={`${onClick ? 'view ' : ''}${product.name} detail`}
      mb={defaultSpacing}
      onClick={
        onClick ? () => productInstance && onClick(productInstance) : undefined
      }
      onChange={(inView: boolean) => {
        if (inView && productInstance) {
          productInstance.impress();
        }
      }}
      triggerOnce
      threshold={0.5}
      delay={1000}
    >
      <Flex
        alignItems="center"
        flexDirection="row"
        onClick={
          isPendingCartProduct(product) && !!onProductImageClick
            ? () => onProductImageClick(product)
            : undefined
        }
        ariaLabel={`cart product ${product.product_id} info`}
      >
        <Box border="transparent-black-five" borderRadius="sm">
          <Image
            altText={productImages[0].imageAlt}
            borderRadius="15px"
            height={isIpad ? '88px' : '120px'}
            src={productImages[0].imageSrc}
            width={isIpad ? '88px' : '120px'}
            sizes="thumbnail"
            responsive
          />
        </Box>
        <Flex flexDirection="column" ml={defaultSpacing}>
          <HighlightedText highlight={searchTerm} variant="body-bold">
            {product.name}
          </HighlightedText>
          {product.brand && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {product.brand}
            </HighlightedText>
          )}
          {product.kind && productKindLabel && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {productKindLabel}
            </HighlightedText>
          )}
          {showCategory && product.category && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {customLineageLabel({
                store,
                lineage: product.category,
              })}
            </HighlightedText>
          )}
          {showPotency && potencyDescription(product, formattedPriceId) && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {potencyDescription(product, formattedPriceId)}
            </HighlightedText>
          )}
          {isSupportedSpecial && product.special_title && (
            <Flex alignItems="center">
              <TagIcon size="sm" color="sunset" />
              <HighlightedText ml={4} color="sunset" highlight={searchTerm}>
                {product.special_title}
              </HighlightedText>
            </Flex>
          )}
          {shouldShowGold && janeGoldTagLabel && (
            <>
              <Flex alignItems="center">
                <JaneGoldIcon size="sm" />
                <HighlightedText
                  ml={4}
                  color="grays-black"
                  highlight={searchTerm}
                >
                  {janeGoldTagLabel}
                </HighlightedText>
              </Flex>
              {isPendingCartProduct(product) &&
                !!product.brand_special_has_segment_rules &&
                !userData?.id && (
                  <Flex alignItems="center">
                    <Typography color="grays-mid">
                      (Sign in for eligibility)
                    </Typography>
                  </Flex>
                )}
            </>
          )}
          {!!product.review_count &&
            product.review_count >= 5 &&
            !!product.aggregate_rating &&
            showRating &&
            store.product_reviews_enabled && (
              <StarRating
                aggregateRating={product.aggregate_rating}
                ratingSuffix={''}
                reviewCount={product.review_count}
              />
            )}
        </Flex>
      </Flex>
      <Flex
        flexDirection="row"
        alignItems={actions ? 'center' : 'start'}
        height="100%"
      >
        {showPrice && (
          <Flex alignItems="flex-end" flexDirection="column" grow={1}>
            {!!discountPrice && (
              <Typography color="sunset" variant="body-bold">
                {formatCurrency(quantity * discountPrice)}
              </Typography>
            )}
            {!!unitPrice &&
              (!discountPrice ||
                !hideStrikethroughPrice(store?.state || '')) && (
                <Typography
                  color={discountPrice ? 'grays-mid' : undefined}
                  variant={discountPrice ? undefined : 'body-bold'}
                  strikeThrough={!!discountPrice}
                >
                  {formatCurrency(quantity * unitPrice)}
                </Typography>
              )}
            {showWeight && <Typography>{productWeight}</Typography>}
          </Flex>
        )}
        {actions}
      </Flex>
    </InViewFlex>
  );
};
